import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  User,
  Building,
  Globe,
  Phone,
  Save,
  Lock,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const ClientSettings = () => {
  const { token, API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [profile, setProfile] = useState({
    company_name: "",
    industry: "",
    company_size: "",
    company_website: "",
    primary_contact_name: "",
    primary_contact_phone: "",
    goals: "",
    challenges: "",
    preferred_communication: "",
    client_timezone: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/client/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfile({
          company_name: data.company_name || "",
          industry: data.industry || "",
          company_size: data.company_size || "",
          company_website: data.company_website || "",
          primary_contact_name: data.primary_contact_name || data.user_name || "",
          primary_contact_phone: data.primary_contact_phone || "",
          goals: data.goals || "",
          challenges: data.challenges || "",
          preferred_communication: data.preferred_communication || "",
          client_timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(profile).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${API}/client/profile?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Profile saved successfully!");
      } else {
        throw new Error("Failed to save profile");
      }
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    setSavingPassword(true);
    try {
      const params = new URLSearchParams({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      
      const response = await fetch(`${API}/auth/change-password?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Password changed successfully!");
        setPasswordForm({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to change password");
      }
    } catch (error) {
      toast.error("Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const industries = [
    "Healthcare Practice",
    "Home Health Agency",
    "Hospital System",
    "Long-term Care",
    "Mental Health",
    "Dental Practice",
    "Physical Therapy",
    "Medical Lab",
    "Healthcare Startup",
    "Life Sciences",
    "Other Healthcare",
  ];

  const companySizes = [
    { value: "1-10", label: "1-10 employees" },
    { value: "11-25", label: "11-25 employees" },
    { value: "26-50", label: "26-50 employees" },
    { value: "51-100", label: "51-100 employees" },
    { value: "101-250", label: "101-250 employees" },
    { value: "250+", label: "250+ employees" },
  ];

  const communicationPreferences = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone Call" },
    { value: "video", label: "Video Call" },
    { value: "chat", label: "Portal Chat" },
  ];

  const timezones = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  ];

  return (
    <ClientLayout activeNav="settings">
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">Settings</h1>
            <p className="text-sm text-gray-400 mt-0.5">Manage your company profile.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-rnb-blue" />
                      Company Information
                    </CardTitle>
                    <CardDescription>
                      Update your company details and contact information.
                    </CardDescription>
                  </div>
                  <Button onClick={handleSaveProfile} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={profile.company_name}
                      onChange={(e) => handleChange("company_name", e.target.value)}
                      placeholder="Your company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select value={profile.industry} onValueChange={(v) => handleChange("industry", v)}>
                      <SelectTrigger>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((ind) => (
                              <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_size">Company Size</Label>
                        <Select value={profile.company_size} onValueChange={(v) => handleChange("company_size", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            {companySizes.map((size) => (
                              <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_website">Website</Label>
                        <div className="flex">
                          <span className="flex items-center px-3 bg-slate-100 border border-r-0 rounded-l-md text-rnb-gray text-sm">
                            <Globe className="h-4 w-4" />
                          </span>
                          <Input
                            id="company_website"
                            value={profile.company_website}
                            onChange={(e) => handleChange("company_website", e.target.value)}
                            placeholder="www.yourcompany.com"
                            className="rounded-l-none"
                          />
                        </div>
                      </div>
                    </div>

                    <hr />

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="primary_contact_name">Primary Contact</Label>
                        <Input
                          id="primary_contact_name"
                          value={profile.primary_contact_name}
                          onChange={(e) => handleChange("primary_contact_name", e.target.value)}
                          placeholder="Full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="primary_contact_phone">Phone Number</Label>
                        <div className="flex">
                          <span className="flex items-center px-3 bg-slate-100 border border-r-0 rounded-l-md text-rnb-gray text-sm">
                            <Phone className="h-4 w-4" />
                          </span>
                          <Input
                            id="primary_contact_phone"
                            value={profile.primary_contact_phone}
                            onChange={(e) => handleChange("primary_contact_phone", e.target.value)}
                            placeholder="(555) 123-4567"
                            className="rounded-l-none"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Communication</Label>
                        <Select value={profile.preferred_communication} onValueChange={(v) => handleChange("preferred_communication", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            {communicationPreferences.map((pref) => (
                              <SelectItem key={pref.value} value={pref.value}>{pref.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Timezone</Label>
                        <Select value={profile.client_timezone} onValueChange={(v) => handleChange("client_timezone", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            {timezones.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <hr />

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="goals">Goals</Label>
                        <Textarea
                          id="goals"
                          value={profile.goals}
                          onChange={(e) => handleChange("goals", e.target.value)}
                          placeholder="What are your main goals for working with us?"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="challenges">Challenges</Label>
                        <Textarea
                          id="challenges"
                          value={profile.challenges}
                          onChange={(e) => handleChange("challenges", e.target.value)}
                          placeholder="What challenges are you currently facing?"
                          rows={3}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Password Change Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="h-5 w-5 text-rnb-blue" />
                      Change Password
                    </CardTitle>
                    <CardDescription>
                      Update your password to keep your account secure.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current_password">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="current_password"
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                          placeholder="Enter your current password"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new_password">New Password</Label>
                        <div className="relative">
                          <Input
                            id="new_password"
                            type={showNewPassword ? "text" : "password"}
                            value={passwordForm.new_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                            placeholder="Enter new password"
                            data-testid="new-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-rnb-gray">Must be at least 8 characters</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password">Confirm New Password</Label>
                        <div className="relative">
                          <Input
                            id="confirm_password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={passwordForm.confirm_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                            placeholder="Confirm new password"
                            data-testid="confirm-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={handleChangePassword} 
                      disabled={savingPassword}
                      data-testid="change-password-btn"
                    >
                      {savingPassword ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientSettings;
